import { ChangeEvent, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetPermissions } from '../../../../modules/auth/core/Auth';
import { applyBriefingValuesVersion, isBriefingV4Enabled } from '../../shared/utils/briefingTemplateVersion';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import type { FormValues } from '../types';
import {
  confirmBriefingPromotionsImport,
  previewBriefingPromotionsImport,
  type BriefingPromotionImportCardPreview,
  type BriefingPromotionImportPreview,
  type BriefingPromotionImportResult,
} from '../services/briefingPromocoesApi';

const HISTORY_ROUTE = '/briefing-promocoes/historico';

const FIELD_LABELS: Record<string, string> = {
  tipoSolicitacao: 'Tipo de solicitação',
  idIFC: 'ID Infracommerce',
  tipoMecanica: 'Tipo de mecânica promocional',
  tipoLista: 'Tipo de lista',
  recompraDisponivel: 'Recompra disponível',
  idListaOriginal: 'ID da lista de referência',
  nomePromo: 'Nome da promoção',
  nomeExibicao: 'Nome de exibição',
  descricao: 'Descrição',
  descricaoInterna: 'Descrição interna',
  numeroCard: 'Número do card',
  tipoDesconto: 'Tipo de desconto',
  valorDesconto: 'Valor do desconto',
  industria: 'Indústria',
  multiPatrocinador: 'Multi patrocinador',
  patrocinadores: 'Patrocinadores e contribuição',
  cumulativa: 'Promoção cumulativa',
  inicioVigencia: 'Início da vigência',
  fimVigencia: 'Fim da vigência',
  promocaoPrioritariaComunicacao: 'Prioritária para comunicação',
  eansPrioritariosComunicacao: 'EANs prioritários',
  briefingComunicacao: 'Briefing para comunicação',
  budgetAtrelado: 'Budget atrelado',
  budgetValor: 'Budget',
  sellersIncluidos: 'Sellers incluídos',
  sellersExcecao: 'Sellers exceção',
  cnpjsIncluidos: 'CNPJs incluídos',
  cnpjsExcecao: 'CNPJs exceção',
  canais: 'Canais',
  tipoPedido: 'Tipo de pedido',
  criterioDesconto: 'Critério de desconto',
  qtdMinima: 'Quantidade mínima',
  qtdMaxima: 'Quantidade máxima',
  valorMinimo: 'Valor mínimo',
  valorMaximo: 'Valor máximo',
  tipoFamilia: 'Tipo de família',
  nomeFamilia: 'Nome da família',
  industriaFamilia: 'Indústria da família',
  obsFamilia: 'Observação da família',
  skusFamilia: 'SKUs da família',
  qtdEANsDistintos: 'Quantidade de EANs distintos',
  qtdMinimaAtivacao: 'Quantidade mínima para ativação',
  qtdMaximaAtivacao: 'Quantidade máxima para ativação',
  qtdFamiliasAtivacao: 'Quantidade de famílias para ativação',
  familiaObrigatoria: 'Família obrigatória',
  posicaoFamilia: 'Posição da família',
  descontoFamilia: 'Desconto da família',
  posicaoLista: 'Posição da lista',
  secaoLista: 'Seção da lista',
  cupom: 'Cupom',
  codigoCupom: 'Código do cupom',
  qtdCupons: 'Quantidade de cupons',
  usosPorCliente: 'Usos por cliente',
  cupomVisivelCarrinho: 'Cupom visível no carrinho?',
};

function hasDisplayValue(value: FormValues[string]): boolean {
  if (Array.isArray(value)) return value.length > 0;
  return String(value ?? '').trim() !== '';
}

function displayValue(value: FormValues[string], key?: string): string {
  if (key === 'cupomVisivelCarrinho') {
    if (String(value) === '0') return 'NÃO';
    if (String(value) === '1') return 'SIM';
  }
  return Array.isArray(value) ? value.join(', ') : String(value ?? '');
}

function roleLabel(role: BriefingPromotionImportCardPreview['role']): string {
  if (role === 'parent') return 'Pai';
  if (role === 'child') return 'Filho';
  return 'Único';
}

function friendlyError(message: string): string {
  const messages: Record<string, string> = {
    BRIEFING_PROMOCOES_IMPORT_FILE_REQUIRED: 'Selecione um arquivo XLSX.',
    BRIEFING_PROMOCOES_IMPORT_XLSX_REQUIRED: 'O arquivo precisa estar no formato XLSX.',
    BRIEFING_PROMOCOES_IMPORT_FILE_TOO_LARGE: 'O arquivo excede o limite de 15 MB.',
    BRIEFING_PROMOCOES_IMPORT_UPLOAD_FAILED: 'Não foi possível receber o arquivo XLSX.',
    BRIEFING_PROMOCOES_IMPORT_INVALID_XLSX: 'Não foi possível ler o arquivo XLSX.',
    BRIEFING_PROMOCOES_IMPORT_FILE_CHANGED: 'O arquivo mudou após a análise. Gere uma nova prévia.',
    BRIEFING_PROMOCOES_IMPORT_VALIDATION_ERROR: 'A prévia possui erros que precisam ser corrigidos.',
  };
  return messages[message] || message;
}

export default function ImportPromotionsDialog() {
  const briefingV4 = isBriefingV4Enabled(GetPermissions());
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BriefingPromotionImportPreview | null>(null);
  const [result, setResult] = useState<BriefingPromotionImportResult | null>(null);
  const [detailCard, setDetailCard] = useState<BriefingPromotionImportCardPreview | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | false>(false);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setDetailCard(null);
    setExpandedGroup(false);
    setLoading(false);
    setConfirming(false);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    if (loading || confirming) return;
    setOpen(false);
    reset();
  };

  const handleFile = (selectedFile: File | null) => {
    setFile(selectedFile);
    setPreview(null);
    setResult(null);
    setExpandedGroup(false);
    setError(null);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    handleFile(event.target.files?.[0] ?? null);
  };

  const handleAnalyze = async () => {
    if (!file) {
      setError('Selecione um arquivo XLSX.');
      return;
    }
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setError('O arquivo precisa estar no formato XLSX.');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      setError('O arquivo excede o limite de 15 MB.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextPreview = await previewBriefingPromotionsImport(file);
      setPreview(nextPreview);
      setExpandedGroup(nextPreview.groups[0]?.id ?? false);
    } catch (requestError) {
      setError(friendlyError(requestError instanceof Error ? requestError.message : 'Não foi possível analisar o arquivo.'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!file || !preview?.canConfirm) return;

    setConfirming(true);
    setError(null);
    try {
      const importResult = await confirmBriefingPromotionsImport(file, preview.checksum);
      setResult(importResult);
    } catch (requestError) {
      const typedError = requestError as Error & { preview?: BriefingPromotionImportPreview };
      if (typedError.preview) setPreview(typedError.preview);
      setError(friendlyError(typedError.message || 'Não foi possível cadastrar as promoções.'));
    } finally {
      setConfirming(false);
    }
  };

  const handleViewHistory = () => {
    const firstParentId = result?.promotions[0]?.parentId;
    handleClose();
    navigate(firstParentId ? `${HISTORY_ROUTE}?briefingId=${firstParentId}` : HISTORY_ROUTE, {
      state: firstParentId ? { selectedBriefingId: String(firstParentId), openBriefing: true } : undefined,
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<UploadFileOutlinedIcon />}
        onClick={() => setOpen(true)}
        sx={{ whiteSpace: 'nowrap' }}
      >
        Importar Promoções
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minHeight: { md: 640 }, maxHeight: '92vh' } }}
      >
        <DialogTitle sx={{ pr: 7 }}>
          {result ? 'Importação concluída' : preview ? 'Confirmar importação de promoções' : 'Importar Promoções'}
          <IconButton onClick={handleClose} disabled={loading || confirming} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />

        <DialogContent sx={{ bgcolor: '#f8fafc' }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {result ? (
            <Stack alignItems="center" spacing={2.5} sx={{ py: 8, textAlign: 'center' }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 72 }} />
              <Typography variant="h5" fontWeight={700}>Promoções cadastradas com sucesso</Typography>
              <Typography color="text.secondary">
                {result.totals.promotions} promoções e {result.totals.cards} cards foram cadastrados.
                {result.totals.children > 0 ? ` ${result.totals.children} cards filhos foram vinculados aos respectivos pais.` : ''}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center">
                {result.promotions.map((promotion) => (
                  <Chip
                    key={`${promotion.templateId}-${promotion.parentId}`}
                    label={`${promotion.name} · ID ${promotion.parentId}`}
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Stack>
          ) : preview ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                  <DescriptionOutlinedIcon color="primary" />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>{preview.fileName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {preview.sheets.length} aba(s) analisada(s)
                    </Typography>
                  </Box>
                  <Chip label={`${preview.totals.promotions} promoções`} color="primary" />
                  <Chip label={`${preview.totals.cards} cards`} />
                  <Chip label={`${preview.totals.children} filhos`} />
                </Stack>
              </Paper>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {preview.templates.map((template) => (
                  <Chip
                    key={template.templateId}
                    label={`${template.templateName}: ${template.promotionCount} promoções / ${template.cardCount} cards`}
                    variant="outlined"
                  />
                ))}
              </Stack>

              {preview.errors.length > 0 && (
                <Alert severity="error">
                  <AlertTitle>O arquivo possui erros e ainda não pode ser importado</AlertTitle>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {preview.errors.map((item) => <li key={item}>{item}</li>)}
                  </Box>
                </Alert>
              )}
              {preview.warnings.length > 0 && (
                <Alert severity="warning">
                  <AlertTitle>Avisos da análise</AlertTitle>
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {preview.warnings.map((item) => <li key={item}>{item}</li>)}
                  </Box>
                </Alert>
              )}

              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                  Tudo que será cadastrado
                </Typography>
                {preview.groups.map((group) => (
                  <Accordion
                    key={group.id}
                    expanded={expandedGroup === group.id}
                    onChange={(_, expanded) => setExpandedGroup(expanded ? group.id : false)}
                    disableGutters
                    sx={{ mb: 1, border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ width: '100%', pr: 1 }}>
                        <Typography fontWeight={700} sx={{ flex: 1 }}>{group.name}</Typography>
                        <Chip size="small" label={group.templateName} />
                        <Chip size="small" variant="outlined" label={`${group.cards.length} card(s)`} />
                        {(group.errors.length > 0 || group.cards.some((card) => card.errors.length > 0)) && (
                          <Chip size="small" color="error" label="Com erro" />
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Linha</TableCell>
                              <TableCell>Vínculo</TableCell>
                              <TableCell>Card</TableCell>
                              <TableCell>Nome de exibição</TableCell>
                              <TableCell>Vigência</TableCell>
                              <TableCell align="right">Detalhes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {group.cards.map((card) => (
                              <TableRow key={card.id} sx={card.errors.length ? { bgcolor: 'rgba(211,47,47,0.06)' } : undefined}>
                                <TableCell>{card.rowNumber}</TableCell>
                                <TableCell><Chip size="small" label={roleLabel(card.role)} variant="outlined" /></TableCell>
                                <TableCell>{card.cardNumber ?? '—'}</TableCell>
                                <TableCell>{displayValue(card.values.nomeExibicao) || '—'}</TableCell>
                                <TableCell>
                                  {displayValue(card.values.inicioVigencia) || '—'}<br />
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    até {displayValue(card.values.fimVigencia) || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Ver todos os campos preenchidos">
                                    <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => setDetailCard(card)}>
                                      Ver campos
                                    </Button>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Stack>
          ) : (
            <Stack spacing={2.5} sx={{ py: 2 }}>
              <Alert severity="info">
                O arquivo pode conter várias promoções, templates diferentes e cards filhos. Primeiro faremos a análise; nenhum cadastro ocorre antes da sua confirmação.
              </Alert>
              <Paper
                variant="outlined"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  handleFile(event.dataTransfer.files?.[0] ?? null);
                }}
                sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed', bgcolor: 'white' }}
              >
                <UploadFileOutlinedIcon color="primary" sx={{ fontSize: 52, mb: 1 }} />
                <Typography variant="h6" fontWeight={700}>Selecione ou arraste um arquivo XLSX</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Limite de 15 MB. Todas as abas válidas serão analisadas.
                </Typography>
                <input ref={inputRef} hidden type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={handleFileChange} />
                <Button variant="contained" onClick={() => inputRef.current?.click()}>Selecionar arquivo</Button>
                {file && (
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
                    <DescriptionOutlinedIcon fontSize="small" />
                    <Typography fontWeight={600}>{file.name}</Typography>
                    <Typography variant="caption" color="text.secondary">({(file.size / 1024 / 1024).toFixed(2)} MB)</Typography>
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}
        </DialogContent>

        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          {result ? (
            <>
              <Button onClick={handleClose}>Fechar</Button>
              <Button variant="contained" onClick={handleViewHistory}>Ver no histórico</Button>
            </>
          ) : preview ? (
            <>
              <Button disabled={confirming} onClick={() => { setPreview(null); setError(null); }}>Escolher outro arquivo</Button>
              <Box sx={{ flex: 1 }} />
              <Button disabled={confirming} onClick={handleClose}>Cancelar</Button>
              <Button
                variant="contained"
                color="success"
                disabled={!preview.canConfirm || confirming}
                onClick={handleConfirm}
                startIcon={confirming ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineIcon />}
              >
                {confirming ? 'Cadastrando...' : `Confirmar ${preview.totals.promotions} promoções`}
              </Button>
            </>
          ) : (
            <>
              <Button disabled={loading} onClick={handleClose}>Cancelar</Button>
              <Button
                variant="contained"
                disabled={!file || loading}
                onClick={handleAnalyze}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <VisibilityOutlinedIcon />}
              >
                {loading ? 'Analisando...' : 'Analisar arquivo'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detailCard)} onClose={() => setDetailCard(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 7 }}>
          Campos da linha {detailCard?.rowNumber}
          <IconButton onClick={() => setDetailCard(null)} sx={{ position: 'absolute', right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailCard?.errors.length ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailCard.errors.map((item) => <div key={item}>{item}</div>)}
            </Alert>
          ) : null}
          <Table size="small">
            <TableBody>
              {detailCard && Object.entries(applyBriefingValuesVersion(detailCard.templateId ?? '', detailCard.values, briefingV4))
                .filter(([key, value]) => key !== 'templateId' && hasDisplayValue(value))
                .map(([key, value]) => (
                  <TableRow key={key}>
                    <TableCell sx={{ width: '38%', fontWeight: 700 }}>{FIELD_LABELS[key] || key}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{displayValue(value, key)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailCard(null)}>Fechar</Button></DialogActions>
      </Dialog>
    </>
  );
}
